import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import { TcSimType } from '../../types/tc-sim/tc-sim.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postTcSims = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      warehouse: { id: number };
      equipment_group: { id: number };
      tcSims: Array<unknown>;
    };
  },
  { state: RootReducerState }
>('tcSims/postTcSims', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/tc-sim/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating SIMs!' });
  }
});

export const postTcSimDetails = createAsyncThunk<TcSimType, { data: unknown }, { state: RootReducerState }>(
  'tcSims/postTcSimDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim`, data);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating SIM!' });
    }
  }
);

export const postSimVerizonActivationStatus = createAsyncThunk<
  { requestId: string },
  { data: { iccid: string; externalSystemInstance: { id: number } } },
  { state: RootReducerState }
>('tcSims/postSimVerizonActivationStatus/no-loader', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ requestId: string }>(
      `${process.env.REACT_APP_API_URL}/verizon/activation-status`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during verification SIM!' });
  }
});

export const getTcSimsList = createAsyncThunk<
  PaginationType<TcSimType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('tcSims/getTcSimsList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<TcSimType>>(
      `${process.env.REACT_APP_API_URL}/tc-sim${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching SIM!' });
  }
});

export const getTcSimDetails = createAsyncThunk<TcSimType, { id: number }, { state: RootReducerState }>(
  'tcSims/getTcSimDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching SIM!' });
    }
  }
);

export const patchTcSimDetails = createAsyncThunk<
  TcSimType,
  { id: number; data: unknown },
  { state: RootReducerState }
>('tcSims/patchTcSimDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating SIM!' });
  }
});

export const deleteTcSimDetails = createAsyncThunk<
  TcSimType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('tcSims/deleteTcSimDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}`);
    if (fetchList) thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting SIM!' });
  }
});

export const patchTcSimContract = createAsyncThunk<
  TcSimType,
  { id: number; contractId: number | null },
  { state: RootReducerState }
>('tcSims/patchTcSimContract', async ({ id, contractId }, thunkAPI) => {
  try {
    const payload = { contract: { id: contractId } };

    const response = await axios.patch<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/contract`, payload);

    thunkAPI.dispatch(getTcSimDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating SIM contract!' });
  }
});

export const postTcSimOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('tcSims/postTcSimOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during SIM ownership transfer!' });
  }
});

export const activateTcSim = createAsyncThunk<TcSimType, { id: number }, { state: RootReducerState }>(
  'tcSims/activateTcSim',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/activate`, {});

      thunkAPI.dispatch(getTcSimDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during activating SIM!' });
    }
  }
);

export const deactivateTcSim = createAsyncThunk<TcSimType, { id: number }, { state: RootReducerState }>(
  'tcSims/deactivateTcSim',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/deactivate`);

      thunkAPI.dispatch(getTcSimDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deactivating SIM!' });
    }
  }
);

export const restoreTcSim = createAsyncThunk<TcSimType, { id: number }, { state: RootReducerState }>(
  'tcSims/restoreTcSim',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/restore`);

      thunkAPI.dispatch(getTcSimDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deactivating SIM!' });
    }
  }
);

export const suspendTcSim = createAsyncThunk<TcSimType, { id: number }, { state: RootReducerState }>(
  'tcSims/suspendTcSim',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<TcSimType>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/suspend`);

      thunkAPI.dispatch(getTcSimDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during suspending SIM!' });
    }
  }
);

export const refreshTcSimStatus = createAsyncThunk<
  void,
  { id: number; fetchList?: boolean },
  { state: RootReducerState; rejectValue: string }
>('tcSims/refreshTcSimStatus', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/refresh-status`);
    if (fetchList) {
      thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    } else {
      thunkAPI.dispatch(getTcSimDetails({ id }));
    }
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during SIM status refresh!' });
  }
});

export const applySuggestedDataPlanType = createAsyncThunk<
  void,
  { id: number; fetchList?: boolean },
  { state: RootReducerState; rejectValue: string }
>('tcSims/applySuggestedDataPlanType', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/apply-suggested-data-plan-type`);
    if (fetchList) {
      thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    } else {
      thunkAPI.dispatch(getTcSimDetails({ id }));
    }
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during suggested data plan type applying!' });
  }
});

export const updateCarrierDataPlanType = createAsyncThunk<
  void,
  { id: number; fetchList?: boolean },
  { state: RootReducerState; rejectValue: string }
>('tcSims/updateCarrierDataPlanType', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.patch(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/carrier-plan`);
    if (fetchList) {
      thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    } else {
      thunkAPI.dispatch(getTcSimDetails({ id }));
    }
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during carrier data plan type applying!' });
  }
});

export const bulkApplySuggestedDataPlanType = createAsyncThunk<
  { results: boolean[] },
  { simIds: number[] },
  { state: RootReducerState; rejectValue: string }
>('tcSims/bulkApplySuggestedDataPlanType', async (body, thunkAPI) => {
  try {
    const response = await axios.post<{ results: boolean[] }>(
      `${process.env.REACT_APP_API_URL}/tc-sim/bulk-apply-suggested-data-plan-type`,
      body
    );
    thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during suggested data plan type bulk-applying!' });
  }
});

export const postTcSimFixService = createAsyncThunk<
  { success: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('tcSims/postTcSimFixService', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/tc-sim/${id}/fix-service`, {});

    if (fetchList) thunkAPI.dispatch(getTcSimsList(thunkAPI.getState().tcSimsReducer.tcSimsRouteParams));
    else thunkAPI.dispatch(getTcSimDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing SIM service!' });
  }
});
